/**
 * Invoice Report (BE-14d-1)
 *
 * FINAL invoices finalised in the range (the day the ledger posted them, not the invoice date), each with what is the
 * TC's own revenue, the government / pass-through part, cost, profit, paid and due. A row opens the invoice.
 * Route: /reports/invoice-report
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, TextField, Autocomplete, MenuItem, Chip } from '@mui/material';
import ReportPage, { formatCurrency, formatDate } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CREDITED', label: 'Credited' },
];
const STATUS_COLOR = { PAID: 'success', PARTIAL: 'warning', UNPAID: 'error', CREDITED: 'info' };

const money = (v) => (Number(v) ? formatCurrency(v) : '-');

const InvoiceReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [to, setTo] = useState(toYmd(new Date()));
  const [customer, setCustomer] = useState(null);
  const [status, setStatus] = useState('');
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    financeReportsService.getCustomers(user.typingCenterId || user.typing_center_id).then(setCustomers).catch(() => {});
  }, []);

  const params = { from, to, customerId: customer?.id, status };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getInvoiceReport({ from, to, customerId: customer?.id, status }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load the invoice report'));
    } finally {
      setLoading(false);
    }
  }, [from, to, customer, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    { key: 'invoiceNumber', label: 'Invoice', render: (r) => r.invoiceNumber },
    { key: 'invoiceDate', label: 'Invoice Date', render: (r) => formatDate(r.invoiceDate) },
    { key: 'finalizedOn', label: 'Finalised', render: (r) => formatDate(r.finalizedOn) },
    { key: 'customer', label: 'Customer', render: (r) => r.customer || '-' },
    { key: 'total', label: 'Total', align: 'right', render: (r) => formatCurrency(r.total), total: (t) => formatCurrency(t.totalAmount) },
    { key: 'vat', label: 'VAT', align: 'right', render: (r) => money(r.vat) },
    { key: 'passThrough', label: 'Government / pass-through', align: 'right', render: (r) => money(r.passThrough) },
    { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue), total: (t) => formatCurrency(t.revenueTotal) },
    { key: 'cost', label: 'Cost', align: 'right', render: (r) => money(r.cost), total: (t) => formatCurrency(t.costTotal) },
    { key: 'profit', label: 'Profit', align: 'right', render: (r) => formatCurrency(r.profit), total: (t) => formatCurrency(t.profitTotal) },
    { key: 'credited', label: 'Credited', align: 'right', render: (r) => money(r.credited) },
    { key: 'paid', label: 'Paid', align: 'right', render: (r) => money(r.paid), total: (t) => formatCurrency(t.paidTotal) },
    { key: 'due', label: 'Due', align: 'right', render: (r) => money(r.due), total: (t) => formatCurrency(t.dueTotal) },
    {
      key: 'paymentStatus', label: 'Status',
      render: (r) => <Chip label={r.paymentStatus} size="small" color={STATUS_COLOR[r.paymentStatus] || 'default'} sx={{ fontSize: '0.7rem', height: 20 }} />,
    },
  ];

  const cards = report ? [
    { label: `Invoiced (${report.count})`, value: formatCurrency(report.totalAmount) },
    { label: 'Revenue', value: formatCurrency(report.revenueTotal), tone: 'good' },
    { label: 'Cost', value: formatCurrency(report.costTotal) },
    { label: 'Profit', value: formatCurrency(report.profitTotal), tone: Number(report.profitTotal) < 0 ? 'bad' : 'good' },
    { label: 'Paid', value: formatCurrency(report.paidTotal) },
    { label: 'Due', value: formatCurrency(report.dueTotal), tone: Number(report.dueTotal) > 0 ? 'warn' : undefined },
  ] : [];

  return (
    <ReportPage
      title="Invoice Report"
      subtitle={`Invoices finalised ${formatDate(from)} – ${formatDate(to)} · revenue is the centre's own income, without VAT and government / pass-through fees`}
      exportType="invoice-report" exportParams={params} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={columns} rows={report?.rows || []} rowKey={(r) => r.invoiceId}
      totals={report} totalsLabel={`TOTALS (${report?.count || 0} invoices)`}
      onRowClick={(r) => navigate(`/invoices/${r.invoiceId}`)}
      emptyMessage="No invoice was finalised in this range."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Finalised from" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Finalised to" type="date" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete size="small" options={customers} value={customer} onChange={(e, v) => setCustomer(v)}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(p) => <TextField {...p} label="Customer / Organization" />} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField select fullWidth size="small" label="Payment status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default InvoiceReport;
